/**
 * 移动端工具函数
 */

export const MOBILE_MAX_WIDTH = 768;

/**
 * 检测是否为移动设备
 */
export function isMobile(): boolean {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // 检测移动设备的用户代理
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  
  // 检测屏幕宽度
  const isSmallScreen = window.innerWidth <= MOBILE_MAX_WIDTH;
  
  return mobileRegex.test(userAgent) || isSmallScreen;
}

/**
 * 检测是否为平板设备
 */
export function isTablet(): boolean {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const tabletRegex = /iPad|Android(?!.*Mobile)/i;
  
  return tabletRegex.test(userAgent) && window.innerWidth >= 768 && window.innerWidth <= 1024;
}

/**
 * 检测是否为桌面设备
 */
export function isDesktop(): boolean {
  return !isMobile() && !isTablet();
}

/**
 * 获取设备类型
 */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (isMobile()) return 'mobile';
  if (isTablet()) return 'tablet';
  return 'desktop';
}

/**
 * 获取视口宽度
 */
export function getViewportWidth(): number {
  if (typeof window === 'undefined') return 0;
  return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
}

/**
 * 获取视口高度
 */
export function getViewportHeight(): number {
  if (typeof window === 'undefined') return 0;
  return window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
}

/**
 * 检测是否支持触摸
 */
export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  );
}

/**
 * 防止移动端滚动穿透
 */
export function preventScroll(prevent = true): void {
  if (typeof document === 'undefined') return;
  
  if (prevent) {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  } else {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  }
}

/**
 * 平滑滚动到顶部
 */
export function scrollToTop(duration = 300): void {
  if (typeof window === 'undefined') return;
  
  const startPosition = window.pageYOffset;
  const startTime = performance.now();
  
  const easeInOutQuad = (time: number, start: number, change: number, duration: number): number => {
    time /= duration / 2;
    if (time < 1) return change / 2 * time * time + start;
    time--;
    return -change / 2 * (time * (time - 2) - 1) + start;
  };
  
  const animateScroll = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const position = easeInOutQuad(elapsed, startPosition, -startPosition, duration);
    
    window.scrollTo(0, position);
    
    if (elapsed < duration) {
      requestAnimationFrame(animateScroll);
    }
  };
  
  requestAnimationFrame(animateScroll);
}

/**
 * 平滑滚动到指定元素
 */
export function scrollToElement(element: HTMLElement | string, offset = 0): void {
  if (typeof window === 'undefined') return;
  
  const targetElement = typeof element === 'string' 
    ? document.querySelector(element) as HTMLElement
    : element;
  
  if (!targetElement) return;
  
  const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - offset;
  
  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth'
  });
}

/**
 * 节流函数
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  
  return function (...args: Parameters<T>) {
    const now = Date.now();
    
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
}

/**
 * 防抖函数
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return function (...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

/**
 * 监听视口大小变化
 */
export function onResize(callback: () => void, delay = 200): () => void {
  if (typeof window === 'undefined') return () => { /* noop */ };
  
  const debouncedCallback = debounce(callback, delay);
  
  window.addEventListener('resize', debouncedCallback);
  
  // 返回取消监听的函数
  return () => {
    window.removeEventListener('resize', debouncedCallback);
  };
}

/**
 * 获取响应式断点
 */
export function getBreakpoint(): 'xs' | 'sm' | 'md' | 'lg' | 'xl' {
  const width = getViewportWidth();
  
  if (width < 576) return 'xs';
  if (width < 768) return 'sm';
  if (width < 1024) return 'md';
  if (width < 1450) return 'lg';
  return 'xl';
}

/**
 * 检测是否在指定断点范围内
 */
export function isBreakpoint(breakpoint: 'xs' | 'sm' | 'md' | 'lg' | 'xl'): boolean {
  return getBreakpoint() === breakpoint;
}

/**
 * 检测是否在指定断点或更小
 */
export function isBreakpointDown(breakpoint: 'sm' | 'md' | 'lg' | 'xl'): boolean {
  const breakpoints = {
    sm: 768,
    md: 1024,
    lg: 1450,
    xl: Infinity
  };
  
  return getViewportWidth() < breakpoints[breakpoint];
}

/**
 * 检测是否在指定断点或更大
 */
export function isBreakpointUp(breakpoint: 'xs' | 'sm' | 'md' | 'lg'): boolean {
  const breakpoints = {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 1024
  };
  
  return getViewportWidth() >= breakpoints[breakpoint];
}
